import { useEffect, useRef, useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { Printer, X } from 'lucide-react';
import { getTheme, setTheme } from '@/lib/theme';
import { SummaryTab } from './SummaryTab';
import { RevenueTab } from './RevenueTab';
import { SubscribersTab } from './SubscribersTab';
import { CatalogInsightsTab } from './CatalogInsightsTab';
import styles from './PlatformProfilePrint.module.css';

const SECTIONS = [
  { id: 'summary', label: 'Summary', Tab: SummaryTab },
  { id: 'revenue', label: 'Revenue', Tab: RevenueTab },
  { id: 'subscribers', label: 'Subscribers', Tab: SubscribersTab },
  { id: 'catalog-insights', label: 'Catalog Insights', Tab: CatalogInsightsTab },
];

/** d360's export width, A4 proportions. */
const PAGE_WIDTH = 1444;
const PAGE_HEIGHT = 2042;

/** Quiet period with nothing fetching before the page counts as loaded. */
const SETTLE_MS = 800;
/** Chart entry animations finish within this. */
const ANIMATION_MS = 1200;
/** Give up waiting on a slow image after this. */
const IMAGE_TIMEOUT_MS = 6000;

/** Resolves once every image on the page has loaded or failed (lazy ones forced eager). */
function imagesLoaded(root: HTMLElement) {
  const imgs = [...root.querySelectorAll('img')];
  imgs.forEach((img) => (img.loading = 'eager'));
  const pending = imgs
    .filter((img) => !img.complete)
    .map(
      (img) =>
        new Promise((done) => {
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        }),
    );
  return Promise.race([Promise.all(pending), new Promise((done) => setTimeout(done, IMAGE_TIMEOUT_MS))]);
}

/**
 * The PDF export, after d360's /pdf profile routes: the hero (from the layout)
 * then every tab, flowing continuously, at a fixed 1444px width. Once
 * nothing is fetching, fonts and images are in and the charts have drawn, it
 * marks itself `data-ready` (what a server-side renderer would wait for) and
 * opens the browser's print dialog, where "Save as PDF" writes the file.
 */
export function PlatformProfilePrint() {
  const rootRef = useRef<HTMLDivElement>(null);
  const fetching = useIsFetching();
  const [ready, setReady] = useState(false);
  const printed = useRef(false);

  // Outside the app Layout, so bootstrap the saved theme here; flag <html> so
  // the print rules (page size, colours) apply to this route only.
  //
  // The page box goes in a <style> appended last to <head> while this route is
  // mounted: an @page rule can't be scoped, and the app stylesheet has its own
  // (@page { margin: 14mm }) that would otherwise win. Zero margins also stop
  // Chrome printing its date / URL header and footer.
  useEffect(() => {
    setTheme(getTheme());
    document.documentElement.setAttribute('data-pdf-export', '');
    const pageStyle = document.createElement('style');
    pageStyle.textContent = `@page { size: ${PAGE_WIDTH}px ${PAGE_HEIGHT}px; margin: 0; }`;
    document.head.appendChild(pageStyle);
    return () => {
      document.documentElement.removeAttribute('data-pdf-export');
      pageStyle.remove();
    };
  }, []);

  useEffect(() => {
    if (fetching > 0 || ready) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      await document.fonts.ready;
      await imagesLoaded(document.body);
      await new Promise((done) => setTimeout(done, ANIMATION_MS));
      if (!cancelled) setReady(true);
    }, SETTLE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [fetching, ready]);

  useEffect(() => {
    if (!ready || printed.current) return;
    printed.current = true;
    window.print();
  }, [ready]);

  return (
    <div ref={rootRef} className={styles.print} data-ready={ready}>
      {/* On screen only: status + manual print / close */}
      <div className={styles.toolbar} data-print-hide>
        <span className={styles.status}>
          {ready ? 'Ready. Choose "Save as PDF" as the destination.' : 'Preparing your PDF…'}
        </span>
        <button type="button" className={styles.toolbarButton} onClick={() => window.print()} disabled={!ready}>
          <Printer size={15} aria-hidden="true" /> Print / Save as PDF
        </button>
        <button type="button" className={styles.toolbarButton} onClick={() => window.close()}>
          <X size={15} aria-hidden="true" /> Close
        </button>
      </div>

      {SECTIONS.map(({ id, label, Tab }) => (
        <section key={id} className={styles.section}>
          <h2 className={styles.sectionTitle}>{label}</h2>
          <Tab />
        </section>
      ))}
    </div>
  );
}
